import {
  InnerBlocks,
  useBlockProps,
  InspectorControls,
  BlockControls,
  JustifyContentControl,
} from '@wordpress/block-editor';

import { resolvePresetValue } from '../../../utils/helpers';
import SourcePanel from './InspectorControls/SourcePanel';
import LayoutPanel from './InspectorControls/LayoutPanel';
import RingPanel from './InspectorControls/RingPanel';
import AvatarAppender from './AvatarAppender';
import DynamicPreview from './DynamicPreview';
import {
  DEFAULT_AVATARS,
  getDefaultAvatarAttributes,
} from '../../_shared/default-avatars';

/**
 * Default avatars inserted with a fresh (manual) Avatar Group.
 *
 * Seeded with real images, not names alone: the whole point of this block is
 * the overlapping stack of faces, and a row of initials tiles reads as the
 * unconfigured state rather than a preview of what the block does. Count
 * matches maxVisible's default (5), so a fresh group fills without immediately
 * showing an overflow tile.
 */
const DEFAULT_TEMPLATE = DEFAULT_AVATARS.slice(0, 5).map((_, index) => [
  'brandy/avatar',
  getDefaultAvatarAttributes(index),
]);

/**
 * Editor for the Avatar Group parent block.
 *
 * In manual mode the avatars are managed as InnerBlocks; in a dynamic mode the
 * avatars are populated from a user query and shown via a live preview. Both
 * render inside the same wrapper carrying the CSS variables the frontend uses,
 * so overlap/size/ring look correct live.
 */
export default function Edit({ attributes, setAttributes, clientId }) {
  const { size, overlap, ringColor, ringWidth, shape, source, justifyContent } = attributes;
  const isManual = (source || 'manual') === 'manual';

  const blockProps = useBlockProps({
    className: `brandy-avatar-group is-shape-${shape} is-justify-${justifyContent || 'left'}`,
    style: {
      '--brandy-avatar-size': `${size}px`,
      '--brandy-avatar-overlap': `${overlap}px`,
      // A palette pick is stored as `var:preset|color|<slug>`, which is a
      // reference rather than CSS; the canvas has to expand it exactly as
      // AvatarGroup::render() does or the ring differs from the frontend.
      '--brandy-avatar-ring-color': resolvePresetValue(ringColor),
      '--brandy-avatar-ring-width': `${ringWidth}px`,
    },
  });

  return (
    <>
      <BlockControls group="block">
        <JustifyContentControl
          value={justifyContent}
          allowedControls={['left', 'center', 'right']}
          onChange={(next) => setAttributes({ justifyContent: next || 'left' })}
        />
      </BlockControls>

      <InspectorControls>
        <SourcePanel attributes={attributes} setAttributes={setAttributes} />
        <LayoutPanel attributes={attributes} setAttributes={setAttributes} />
        <RingPanel attributes={attributes} setAttributes={setAttributes} />
      </InspectorControls>

      <div {...blockProps}>
        {isManual ? (
          <InnerBlocks
            allowedBlocks={['brandy/avatar']}
            template={DEFAULT_TEMPLATE}
            orientation="horizontal"
            templateLock={false}
            renderAppender={() => <AvatarAppender clientId={clientId} />}
          />
        ) : (
          <DynamicPreview attributes={attributes} />
        )}
      </div>
    </>
  );
}
